import React, { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Calendar, Users, CheckCircle, XCircle, Clock, AlertCircle } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';

interface Enrollment {
  id: string;
  participant_id: string;
  profiles: {
    first_name: string;
    last_name: string;
    email: string;
  };
}

interface AttendanceRecord {
  id: string;
  enrollment_id: string;
  session_date: string;
  session_time: string;
  status: string;
  notes?: string;
}

interface AttendanceManagerProps {
  courseId: string;
}

export const AttendanceManager: React.FC<AttendanceManagerProps> = ({ courseId }) => {
  const [enrollments, setEnrollments] = useState<Enrollment[]>([]);
  const [attendanceRecords, setAttendanceRecords] = useState<AttendanceRecord[]>([]);
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [selectedTime, setSelectedTime] = useState('09:00');
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    fetchEnrollments();
    fetchAttendanceRecords();
  }, [courseId, selectedDate]);

  const fetchEnrollments = async () => {
    try {
      const { data, error } = await supabase
        .from('enrollments')
        .select(`
          id,
          participant_id,
          profiles!enrollments_participant_id_fkey (
            first_name,
            last_name,
            email
          )
        `)
        .eq('course_id', courseId)
        .eq('status', 'enrolled');

      if (error) throw error;
      setEnrollments(data || []);
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to fetch enrollments",
        variant: "destructive"
      });
    }
  };

  const fetchAttendanceRecords = async () => {
    try {
      const { data, error } = await supabase
        .from('attendance_records')
        .select('*')
        .in('enrollment_id', enrollments.map(e => e.id))
        .eq('session_date', selectedDate);

      if (error) throw error;
      setAttendanceRecords(data || []);
    } catch (error) {
      console.error('Error fetching attendance:', error);
    }
  };

  const updateAttendance = async (enrollmentId: string, status: string, notes?: string) => {
    setLoading(true);
    try {
      const existingRecord = attendanceRecords.find(r => r.enrollment_id === enrollmentId);
      
      if (existingRecord) {
        const { error } = await supabase
          .from('attendance_records')
          .update({ 
            status, 
            notes,
            session_time: selectedTime,
            recorded_by: (await supabase.auth.getUser()).data.user?.id
          })
          .eq('id', existingRecord.id);

        if (error) throw error;
      } else {
        const { error } = await supabase
          .from('attendance_records')
          .insert({
            enrollment_id: enrollmentId,
            session_date: selectedDate,
            session_time: selectedTime,
            status,
            notes,
            recorded_by: (await supabase.auth.getUser()).data.user?.id
          });

        if (error) throw error;
      }

      await fetchAttendanceRecords();
      toast({
        title: "Success",
        description: "Attendance updated successfully"
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to update attendance",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const getAttendanceRecord = (enrollmentId: string) => {
    return attendanceRecords.find(r => r.enrollment_id === enrollmentId);
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'present': return <CheckCircle className="h-4 w-4 text-green-500" />;
      case 'absent': return <XCircle className="h-4 w-4 text-red-500" />;
      case 'late': return <Clock className="h-4 w-4 text-yellow-500" />;
      case 'excused': return <AlertCircle className="h-4 w-4 text-blue-500" />;
      default: return null;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'present': return 'bg-green-100 text-green-800';
      case 'absent': return 'bg-red-100 text-red-800';
      case 'late': return 'bg-yellow-100 text-yellow-800';
      case 'excused': return 'bg-blue-100 text-blue-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Users className="h-5 w-5" />
          Attendance Management
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="flex gap-4 items-end">
          <div className="flex-1">
            <label className="text-sm font-medium">Session Date</label>
            <Input
              type="date"
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
            />
          </div>
          <div className="flex-1">
            <label className="text-sm font-medium">Session Time</label>
            <Input
              type="time"
              value={selectedTime}
              onChange={(e) => setSelectedTime(e.target.value)}
            />
          </div>
        </div>

        <div className="space-y-4">
          {enrollments.map((enrollment) => {
            const record = getAttendanceRecord(enrollment.id);
            return (
              <div key={enrollment.id} className="border rounded-lg p-4 space-y-3">
                <div className="flex justify-between items-start">
                  <div>
                    <h4 className="font-medium">
                      {enrollment.profiles.first_name} {enrollment.profiles.last_name}
                    </h4>
                    <p className="text-sm text-muted-foreground">{enrollment.profiles.email}</p>
                  </div>
                  {record && (
                    <Badge className={getStatusColor(record.status)}>
                      <div className="flex items-center gap-1">
                        {getStatusIcon(record.status)}
                        {record.status}
                      </div>
                    </Badge>
                  )}
                </div>

                <div className="flex gap-2">
                  <Button 
                    size="sm" 
                    variant={record?.status === 'present' ? 'default' : 'outline'}
                    onClick={() => updateAttendance(enrollment.id, 'present')}
                    disabled={loading}
                  >
                    Present
                  </Button>
                  <Button 
                    size="sm" 
                    variant={record?.status === 'absent' ? 'destructive' : 'outline'}
                    onClick={() => updateAttendance(enrollment.id, 'absent')}
                    disabled={loading}
                  >
                    Absent
                  </Button>
                  <Button 
                    size="sm" 
                    variant={record?.status === 'late' ? 'secondary' : 'outline'}
                    onClick={() => updateAttendance(enrollment.id, 'late')}
                    disabled={loading}
                  >
                    Late
                  </Button>
                  <Button 
                    size="sm" 
                    variant={record?.status === 'excused' ? 'secondary' : 'outline'}
                    onClick={() => updateAttendance(enrollment.id, 'excused')}
                    disabled={loading}
                  >
                    Excused
                  </Button>
                </div>

                {record && (
                  <div>
                    <Textarea
                      placeholder="Add notes..."
                      value={record.notes || ''}
                      onChange={(e) => {
                        const updatedRecords = attendanceRecords.map(r => 
                          r.id === record.id ? { ...r, notes: e.target.value } : r
                        );
                        setAttendanceRecords(updatedRecords);
                      }}
                      onBlur={() => updateAttendance(enrollment.id, record.status, record.notes)}
                      className="min-h-[60px]"
                    />
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {enrollments.length === 0 && (
          <div className="text-center py-8 text-muted-foreground">
            No enrolled participants found for this course.
          </div>
        )}
      </CardContent>
    </Card>
  );
};